import { useEffect } from 'react';
import { Link } from 'react-router-dom';

type Section = { title: string; paragraphs?: string[]; bullets?: string[] };

function MissionPage({
  eyebrow, title, subtitle, image, sections, siblingHref, siblingLabel,
}: {
  eyebrow: string; title: string; subtitle: string; image: string; sections: Section[];
  siblingHref: string; siblingLabel: string;
}) {
  useEffect(() => { document.title = `${title}｜AUM1001 Matrix`; }, [title]);
  return <div className="min-h-screen bg-background-50 text-foreground-900">
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/30 bg-background-50/90 backdrop-blur-sm">
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="text-base tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>AUM1001 Matrix</Link>
        <div className="flex items-center gap-2">
          <Link to="/abaa-witness" className="hidden rounded-md px-3 py-2 text-sm text-foreground-600 hover:bg-white sm:block">ABAA 見證</Link>
          <Link to={siblingHref} className="rounded-md border border-primary-300 bg-white/80 px-3 py-2 text-sm text-primary-800">{siblingLabel}</Link>
        </div>
      </nav>
    </header>

    <main>
      <section className="relative isolate flex min-h-[620px] items-center overflow-hidden px-5 pb-16 pt-28 text-center md:min-h-[720px]">
        <img src={image} alt="宇宙生命樹" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/40 via-white/30 to-white/95" />
        <div className="mx-auto max-w-4xl">
          <p className="text-xs tracking-[0.24em] text-foreground-600">{eyebrow}</p>
          <h1 className="mt-5 text-3xl leading-tight md:text-5xl" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h1>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-8 text-foreground-800 md:text-lg">{subtitle}</p>
          <a href="#mission" className="mt-8 inline-block rounded-md bg-foreground-900 px-6 py-3 text-sm text-white">閱讀使命與共建</a>
        </div>
      </section>

      <div id="mission" className="scroll-mt-20">
        {sections.map((section, index) => <section key={section.title} className={index % 2 ? 'bg-background-100 py-16 md:py-20' : 'py-16 md:py-20'}>
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-2xl leading-tight md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>{section.title}</h2>
            {section.paragraphs && <div className="mt-7 space-y-5 text-base leading-8 text-foreground-700">
              {section.paragraphs.map(p => <p key={p}>{p}</p>)}
            </div>}
            {section.bullets && <ul className="mt-7 grid gap-3 md:grid-cols-2">
              {section.bullets.map(item => <li key={item} className="rounded-xl border border-background-200 bg-white/80 p-4 text-sm leading-7 text-foreground-700">{item}</li>)}
            </ul>}
          </div>
        </section>)}
      </div>

      <section className="bg-foreground-900 py-14 text-white">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <p className="text-sm leading-7 text-white/75">兩項使命各有不同的建造內容與資源需求，卻共同服事於同一棵宇宙生命樹。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/abaa-witness" className="rounded-md bg-white px-5 py-3 text-sm text-foreground-900">回到 ABAA 見證</Link>
            <Link to={siblingHref} className="rounded-md border border-white/40 px-5 py-3 text-sm text-white">{siblingLabel}</Link>
          </div>
        </div>
      </section>
    </main>
  </div>;
}

const humanParadiseSections: Section[] = [
  {
    title: '從《1001一之門》走向在地如天',
    paragraphs: [
      '《1001一之門》不只說明個體如何通過 6.5D「一之門」，也說明已經開始內建衷愛軸的生命，如何從個人修馨走向子場與母場的共同建造。',
      '在 4.5D–6.5D，生命經由 HEB123，從錨定「一之軸理」、恢復「成所作智」開始，逐步建立「衷愛軸」，可以承接宇炁，使個體不再只是追求自己的花、果實與種子，而能重新連回「宇宙生命樹的枝幹」，成為源頭生命供應的在地管道。',
      '這條路徑稱為「在地如天」——Axial Below, As Above。它不是模仿一個想像中的天堂，而是學習生命本身如何運作，將源頭創生的原理落實於家庭、教育、產業、土地、照護、科技與共同治理之中。',
    ],
  },
  {
    title: '聖子基金會與 HEB123',
    paragraphs: [
      '聖子基金會承接《1001一之門》的實踐工程，推動 4.5D–6.5D HEB123、子場建造、母場人間天堂及在地如天。',
      'HEB123 是「基督意識」與「馨菩提道」的具身落實。它超越傳統宗教主要以個人修練、信仰認同、儀式或教義傳講為中心的方式，使「修馨」真正進入家庭、工作、產業、土地、組織與共同治理，在每一次選擇、承擔、合作、創新及資源運用中接受檢驗。',
      '個體經由 HEB1 錨定「一之軸理」、恢復「成所作智」，使小 i 主領者開始出現；再經由 HEB2、HEB3 逐步內建「衷愛軸」，使生命不只完成個人的覺醒，也能成為子場與母場的供應管道。這才是「人類首次可以帶著肉身揚升 >5D」的真實意義。',
    ],
  },
  {
    title: '仿生創新與生命樹真價值',
    paragraphs: [
      '森林透過根系交換養分；菌絲連結土地與植物；水、陽光、空氣、微生物與動植物彼此循環。母場人間天堂所說的「仿生創新」，就是將這種生命運作方式帶回人類社會。',
      '母場產業不再以無限消耗人與地球為代價，而是在「創新價值」的同時，修復土地、關係與社會。每一項產業除了創新產品、服務與收益，也必須錨定「一之軸理」，辨識自己在宇宙生命樹主幹進入地球肢體的位置，找出生命、團隊、土地與產業在宇宙光炁、宇炁引領下，各階段要展開的「生命樹真價值」。',
      '創新價值不只追求新產品、新技術或更高獲利，而是使經濟活動重新具有生命方向：在創新收入與工作機會的同時，修復生態、關係、教育、健康與共同體。',
    ],
    bullets: [
      '是否支持生命成長？', '是否修復而不是透支土地？', '是否促進信任與合作？',
      '是否使個體更自主，而不是產生新的依賴？', '是否能供應子場、母場與下一代？',
      '是否符合源頭創生目的，成為宇宙生命樹可持續流動的一部分？',
    ],
  },
  {
    title: '建立子場與母場',
    paragraphs: [
      '子場是實踐「在地如天」的最小生命共同體。一個家庭、工作團隊、學校、農場、企業、療育據點或共同生活與工作的夥伴，都可能成為子場。',
      '子場不是以口號或情感歸屬建立，而是由能夠守在中軸、主動承擔並完成事情的人共同形成。它不是依賴共同體供養個人的避難所，而是每一個生命學習自主、互助、共創與共同承擔的實踐場。',
      '當多個子場能夠穩定運作、彼此交換資源並共同承擔更大的生命需要，就能逐步形成母場。母場不取代子場的自主性，而是提供單一家庭或團隊難以獨自建造的共同生活基礎。',
    ],
    bullets: [
      '土地、居住、自然農業與食物供應', '水、能源、交通與循環經濟', '兒童到長者的終身教育',
      '健康維護、宇炁療育與身心照護', '創業育成、工作媒合與共同產業', '文化、音樂、藝術、舞蹈與節慶',
      'AI、數位平台與透明資源管理', '子場之間的協作、交換、爭議處理與共同治理',
    ],
  },
  {
    title: '赤子班 生態村與馨烏托邦',
    paragraphs: [
      '母場「人間天堂」可以先從一座「赤子班」或「生態村」開始。它不是封閉的宗教聚落，也不是要求所有人過同一種生活的集體社區，而是一個開放、可學習、可工作、可交換、可短期停留，也可長期共建的衷愛豐饒生命基地。',
      '土地、農業、教育、照護、產業、藝術與科技在同一場域中重新連結。居住者不是只消費服務，而是依自己的生命角色與能力，參與場域供應、維護、創新與治理。',
      '當不同地區的生態村、企業、學校、農場與生活共同體，按照共同生命原則彼此聯網，就能形成多中心、分散式的馨烏托邦加邦連網。每個據點保有在地特色與自主權；能在地完成的，就在地完成，需要共同完成的，才交由更大的聯網處理。',
    ],
  },
  {
    title: '讓人類不再有後顧之憂',
    paragraphs: [
      '「不再有後顧之憂」不是讓人放棄工作、等待共同體供養，也不是承諾人生從此沒有風險與困難。它要解決的是：一個願意努力、學習、工作並貢獻的人，不應因疾病、老化、失業、育兒、居住或生命轉換，而失去基本尊嚴與重新站起來的機會。',
      '當基本生存焦慮不再吞噬大部分生命能量，人才能把更多時間與能力投入學習、創新、藝術、服事、家庭、社會及文明共建。這不是降低人的責任，而是讓人有條件承擔更高層次的角色。',
    ],
  },
  {
    title: '使命 募建與聯網共治',
    paragraphs: [
      '聖子基金會的任務，是募集並治理母場所需要的土地、建築、人才、教育、產業、科技與營運資源，建立可以實踐、驗證、修正、複製並彼此聯網的人間天堂示範場。',
      '母場不是由 ABAA 永久帶領所有人，而是由逐步成熟的 Initiator——i 主領者，在共同軸理與透明治理下，分層承擔、在地建造並彼此聯網。',
    ],
    bullets: [
      '尋找土地、退場校園與可活化建築', '建立第一個母場生態村示範基地', '培育子場、母場 i 主領者',
      '整合農業、教育、健康、建築、能源、科技與產業團隊', '募集土地、設備、營運與人才培育資源',
      '建立捐款、投資、收益與公益資源分流制度', '公布預算、工程進度、治理架構與階段成果',
    ],
  },
];

const lightTowerSections: Section[] = [
  {
    title: '為什麼要寫「1001宇宙書架」',
    paragraphs: [
      '書寫「1001宇宙書架」，不只是為了傳遞一套思想或出版一系列書籍，而是為了及時募集建造資源，在馨地球的十二脈輪位置，逐步建造二至十二座「宇光之塔」。',
      '「宇光之塔」是能夠承接「源頭宇光」——超高頻率、能量與振動——的實質建物。它們承接的是過去地球金字塔曾經擔負、如今需要重新升級的行星功能：使宇光直接進入地球，更新「地球網格」，逐步移除舊有黑暗網格，協助穩定「行星生命系統」。',
      '源頭宇光經由「宇光之塔」進入地球之後，降維至 6.5D，形成「宇宙生命樹供應」的全球布輸網路，使不同地區、不同層次的生命與場域，都能在可承載的頻率中接收供應。',
    ],
  },
  {
    title: '宇光之塔 宇光之柱與宇宙燈塔',
    paragraphs: [
      '「宇光之塔」是承接源頭宇光、更新地球網格的實質建物。',
      '「宇光之柱」是內建「衷愛軸」、能夠承接宇光的「馨真人」。當多位宇光之柱聯網共振，才逐步形成「宇宙燈塔」與 7–8–9D 父場，展開在地如宇——Axial Below, Aligning Above——的宇神共創與星際文明。',
      '「宇光之塔」不只是一座單獨的物質建築；「宇宙燈塔」也不只是「宇光之柱」的集合。三者分別對應行星建物、個體生命管道與集體父場，各有不同本體、功能與建造條件。',
    ],
  },
  {
    title: '宇之光基金會與父場工程',
    paragraphs: [
      '宇之光基金會承接《1001宇之光》的父場工程，推動 7–8–9D 在地如宇——Axial Below, Aligning Above。',
      '宇之光基金會負責宇光之塔的土地、建築、科技、能源、工程、治理及全球布局；同時培育與連結宇光之柱，使實質建物、生命管道與父場文明相互配合，逐步走向宇神共創與星際文明。',
      '書籍、Portal、生命培育、場域建造與宇光之塔募建，不是彼此分離的計畫，而是同一套「宇宙生命樹回鄉系統」在不同層次展開的同步工程。',
    ],
  },
  {
    title: '三本同步工程手冊',
    paragraphs: [
      '《1001旋轉門》、《1001一之門》與《1001宇之光》，一方面使個體從 1D–5D「守在中軸」，經由 HEB123 進入 6.5D「宇宙生命樹供應」；另一方面培育能夠內建「衷愛軸」的「宇光之柱」，為「宇宙燈塔」、父場及「星際文明」準備真正可以承載宇光的人。',
      '聖子基金會使人類學會「在地如天」，建造能夠承接供應的人與母場；宇之光基金會使行星走向「在地如宇」，建造承接宇光的行星設施與父場文明。兩者同源而分工，共同完成從個體回鄉、集體回歸，到文明與行星更新的完整工程。',
    ],
  },
];

export function HumanParadisePage() {
  return <MissionPage eyebrow="SON FOUNDATION · 4.5D–6.5D" title="母場 人間天堂使命與共建" subtitle="由《1001一之門》走向在地如天，以 HEB123、仿生創新、子場與母場，建立可實踐、可驗證、可聯網的生命共同體。" image="/images/trilogy/og-tree.jpg" sections={humanParadiseSections} siblingHref="/mission/light-towers" siblingLabel="父場宇光之塔" />;
}

export function LightTowerPage() {
  return <MissionPage eyebrow="LIGHT OF THE COSMOS FOUNDATION · 7D–9D" title="父場 宇光之塔使命與募建" subtitle="由《1001宇之光》走向在地如宇，逐步建造二至十二座宇光之塔，並培育宇光之柱、宇宙燈塔與父場文明。" image="/images/trilogy/ow-tree.jpg" sections={lightTowerSections} siblingHref="/mission/human-paradise" siblingLabel="母場人間天堂" />;
}
